import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Checkbox,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
  MenuItem,
  Select,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  Collapse,
  CircularProgress,
  FormControlLabel,
  Tooltip,
  InputLabel,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import SettingsIcon from '@mui/icons-material/Settings';
import DownloadIcon from '@mui/icons-material/Download';
import StarIcon from '@mui/icons-material/Star';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { VerticalDropdown } from '@src/components';
import {
  fetchLanes,
  rebuildLanes,
  simulateLanes,
  updateLane,
  fetchLanesExport,
  createLane,
  updateLaneAssignment,
  deleteLane,
  fetchLaneConflicts,
  fetchLaneRuns,
  fetchLaneMap,
  fetchLaneSweep,
  revertLanes,
  removeClientFromLanes,
} from '@src/api';
import useClientAutocomplete from '@src/hooks/useClientAutocomplete';
import { MapContainer, TileLayer, GeoJSON } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

import { fetchVerticals } from '@src/api/actions/verticals';

const Section = ({ title, action, children }) => (
  <Card
    sx={{
      borderRadius: 2.5,
      border: '1px solid',
      borderColor: 'divider',
      boxShadow: '0 18px 32px rgba(15, 23, 42, 0.08)',
    }}
  >
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        px: 2.5,
        py: 1.75,
        backgroundColor: 'background.default',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography variant="h6" fontWeight={700}>
        {title}
      </Typography>
      {action}
    </Box>
    <CardContent>{children}</CardContent>
  </Card>
);

const LANE_COLORS = ['#f8fafc', '#f0f9ff', '#f5f3ff', '#fefce8', '#f0fdf4'];
const formatDollars0 = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});
const PARENT_COLORS = ['#0ea5e9', '#22c55e', '#a855f7', '#f97316', '#ef4444', '#14b8a6', '#eab308', '#3b82f6'];

const colorForParent = (name) => {
  if (!name) return { bg: 'rgba(226,232,240,0.6)', border: '#cbd5e1', text: '#334155' };
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  }
  const color = PARENT_COLORS[hash % PARENT_COLORS.length];
  return {
    bg: `${color}26`, // add low opacity
    border: `${color}66`,
    text: '#0f172a',
  };
};

const parseSimulationInput = (raw = '') => {
  const zipPattern = /^\d{5}(-\d{4})?$/;
  const zipNinePattern = /^\d{9}$/;
  const isZip = (value) => zipPattern.test(value) || zipNinePattern.test(value);
  const zips = new Set();
  const clients = new Set();
  const rows = [];

  const lines = (raw || '').split(/\r?\n/);
  lines.forEach((line) => {
    const trimmed = (line || '').trim();
    if (!trimmed) return;

    if (trimmed.includes(',')) {
      const parts = trimmed.split(',').map((part) => part.trim()).filter(Boolean);
      if (!parts.length) return;
      const zipParts = parts.filter((part) => isZip(part));
      if (zipParts.length === parts.length) {
        zipParts.forEach((part) => zips.add(part));
        return;
      }
      if (isZip(parts[0])) {
        zips.add(parts[0]);
        const remainder = parts.slice(1).join(' ').trim();
        if (remainder) {
          clients.add(remainder);
          rows.push({ zip: parts[0], client: remainder });
        }
        return;
      }
      parts.forEach((part) => {
        if (isZip(part)) {
          zips.add(part);
        } else {
          clients.add(part);
        }
      });
      return;
    }

    if (trimmed.includes('\t')) {
      const parts = trimmed.split('\t').map((part) => part.trim()).filter(Boolean);
      if (!parts.length) return;
      if (isZip(parts[0])) {
        zips.add(parts[0]);
        const remainder = parts.slice(1).join(' ').trim();
        if (remainder) {
          clients.add(remainder);
          rows.push({ zip: parts[0], client: remainder });
        }
      } else {
        parts.forEach((part) => clients.add(part));
      }
      return;
    }

    const tokens = trimmed.split(/\s+/).map((token) => token.trim()).filter(Boolean);
    if (!tokens.length) return;
    if (isZip(tokens[0]) && tokens.length > 1) {
      zips.add(tokens[0]);
      const remainder = tokens.slice(1).join(' ').trim();
      if (remainder) {
        clients.add(remainder);
        rows.push({ zip: tokens[0], client: remainder });
      }
      return;
    }
    tokens.forEach((token) => {
      if (isZip(token)) {
        zips.add(token);
      } else {
        clients.add(token);
      }
    });
  });

  return { zips: Array.from(zips), clients: Array.from(clients), rows };
};

function LaneCard({
  lane,
  lanes,
  onLockToggle,
  onMove,
  onConflictsForClient,
  onEditStart,
  onEditCancel,
  onEditSave,
  editingLaneId,
  editingLaneName,
  setEditingLaneName,
  savingLane,
  onDeleteLane,
  deletingLaneId,
  verticalName,
  reservingLaneId,
  onReservedToggle,
  onRemoveClient,
  removingClientId,
}) {
  const laneSpend = (lane.assignments || []).reduce(
    (sum, a) => sum + Number(a.spend_weight || 0),
    0
  );
  const [showActions, setShowActions] = useState(false);
  const laneColor =
    LANE_COLORS[
      Math.abs((lane.id || 0) + (lane.name || '').length) % LANE_COLORS.length
    ];
  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 2,
        borderColor: 'divider',
        backgroundColor: laneColor,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        {editingLaneId === lane.id ? (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
            <TextField
              size="small"
              value={editingLaneName}
              onChange={(e) => setEditingLaneName(e.target.value)}
              sx={{ minWidth: 160 }}
            />
            <IconButton
              size="small"
              color="primary"
              disabled={savingLane || !editingLaneName.trim()}
              onClick={() => onEditSave(lane.id)}
            >
              <CheckIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={onEditCancel} disabled={savingLane}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        ) : (
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={700}>
              {lane.name}
            </Typography>
            {lane.reserved ? <Chip size="small" label="Reserved" /> : null}
            <IconButton size="small" onClick={() => onEditStart(lane)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Stack>
        )}
        <Stack direction="row" spacing={1} mt={0.5}>
          <Chip size="small" label={`${lane.client_count || 0} clients`} />
          <Chip size="small" label={`${lane.total_zip_count || 0} zips`} />
          <Chip
            size="small"
            label={`Spend (90d) $${formatDollars0.format(laneSpend)}`}
            sx={{ flexShrink: 0 }}
          />
          {lane.assignments.length === 0 ? (
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={() => onDeleteLane(lane.id)}
              disabled={deletingLaneId === lane.id}
            >
              {deletingLaneId === lane.id ? 'Deleting…' : 'Delete'}
            </Button>
          ) : null}
          <Button
            size="small"
            variant="text"
            startIcon={<SettingsIcon fontSize="small" />}
            onClick={() => setShowActions((prev) => !prev)}
          >
            {showActions ? 'Hide' : 'Manage'}
          </Button>
        </Stack>
      </Box>
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {showActions ? (
          <Box>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={Boolean(lane.reserved)}
                  onChange={(e) => onReservedToggle(lane, e.target.checked)}
                  disabled={reservingLaneId === lane.id}
                />
              }
              label="Reserved lane (skip auto-assign)"
            />
          </Box>
        ) : null}
        {lane.assignments.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No assignments yet.
          </Typography>
        ) : (
          lane.assignments.map((assignment) => (
                <Box
                  key={`${assignment.client_id}-${assignment.client_territory_id || 'all'}`}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    px: 1.5,
                    py: 1,
                  }}
                >
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography fontWeight={700}>{assignment.client_name}</Typography>
                {Number(assignment.overlap_count || 0) === 0 && Number(assignment.zip_count || 0) > 0 ? (
                  <Tooltip title="100% unique ZIPs for this vertical" arrow>
                    <StarIcon fontSize="small" sx={{ color: '#f59e0b' }} />
                  </Tooltip>
                ) : null}
                {assignment.parent_company_name ? (
                  (() => {
                    const c = colorForParent(assignment.parent_company_name);
                    return (
                      <Chip
                        size="small"
                        variant="outlined"
                        label={assignment.parent_company_name}
                        sx={{
                          borderColor: c.border,
                          color: c.text,
                          backgroundColor: c.bg,
                        }}
                      />
                    );
                  })()
                ) : null}
                {verticalName === 'Bath' && assignment.bath_manufacturer ? (
                  <Chip
                    size="small"
                    label={assignment.bath_manufacturer}
                  />
                ) : null}
              </Stack>
              {assignment?.ad_offers?.filter((offer) =>
                ['offer', 'Ad Offer'].includes(offer.offer_type)
              ).length ? (
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="center"
                  flexWrap="wrap"
                  sx={{ mt: 0.25 }}
                >
                  <Typography variant="caption" color="text.secondary">
                    Ad offer:
                  </Typography>
                  {assignment.ad_offers
                    .filter((offer) => ['offer', 'Ad Offer'].includes(offer.offer_type))
                    .map((offer) => {
                      const label = offer.label || offer.name || offer.custom_label || 'Offer';
                      return (
                        <Chip
                          key={offer.id || offer.name}
                          size="small"
                          variant="outlined"
                          label={label}
                          sx={{ backgroundColor: 'background.paper' }}
                        />
                      );
                    })}
                </Stack>
              ) : null}
              <Typography variant="body2" color="text.secondary">
                {assignment.zip_count} zips • Spend (90d) ${formatDollars0.format(Number(assignment.spend_weight || 0))}
              </Typography>
              {assignment.locked ? (
                <Chip size="small" label="Locked" color="default" sx={{ mt: 0.5, alignSelf: 'flex-start' }} />
              ) : null}
              <Collapse in={showActions}>
                <Stack direction="row" spacing={1} mt={1}>
                  <Button
                    size="small"
                    variant={assignment.locked ? 'contained' : 'outlined'}
                    onClick={() => onLockToggle(assignment)}
                  >
                    {assignment.locked ? 'Unlock' : 'Lock'}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={assignment.locked}
                    onClick={() => onMove(assignment, 'create-new')}
                  >
                    New lane
                  </Button>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() =>
                      onConflictsForClient({
                        client_id: assignment.client_id,
                        client_name: assignment.client_name,
                      })
                    }
                  >
                    Conflicts
                  </Button>
                  <Select
                    size="small"
                    value={assignment.lane_id || lane.id}
                    onChange={(e) => onMove(assignment, e.target.value)}
                    sx={{ minWidth: 140 }}
                    disabled={assignment.locked}
                  >
                    {lanes.map((l) => (
                      <MenuItem key={l.id} value={l.id}>
                        {l.name}
                      </MenuItem>
                    ))}
                  </Select>
                  <Button
                    size="small"
                    color="error"
                    variant="text"
                    startIcon={<DeleteOutlineIcon fontSize="small" />}
                    disabled={removingClientId === assignment.client_id}
                    onClick={() => onRemoveClient(assignment)}
                  >
                    {removingClientId === assignment.client_id ? 'Removing…' : 'Remove'}
                  </Button>
                </Stack>
              </Collapse>
            </Box>
          ))
        )}
      </CardContent>
    </Card>
  );
}

function ClientLanesPage() {
  const [verticalId, setVerticalId] = useState(null);
  const [lanes, setLanes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rebuilding, setRebuilding] = useState(false);
  const [zipInput, setZipInput] = useState('');
  const [simulation, setSimulation] = useState(null);
  const [error, setError] = useState(null);
  const [rebuildMode, setRebuildMode] = useState('full');
  const [overlapTolerance, setOverlapTolerance] = useState(0.05);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [moving, setMoving] = useState(false);
  const [locking, setLocking] = useState(false);
  const [creatingLane, setCreatingLane] = useState(false);
  const [editingLaneId, setEditingLaneId] = useState(null);
  const [editingLaneName, setEditingLaneName] = useState('');
  const [savingLane, setSavingLane] = useState(false);
  const [reservingLaneId, setReservingLaneId] = useState(null);
  const [rebuildDialogOpen, setRebuildDialogOpen] = useState(false);
  const [selectedClients, setSelectedClients] = useState([]);
  const [deletingLaneId, setDeletingLaneId] = useState(null);
  const [simulateDialogOpen, setSimulateDialogOpen] = useState(false);
  const [conflictsDialogOpen, setConflictsDialogOpen] = useState(false);
  const [conflictsData, setConflictsData] = useState(null);
  const [conflictsLoading, setConflictsLoading] = useState(false);
  const [conflictsClient, setConflictsClient] = useState(null);
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [mapDialogOpen, setMapDialogOpen] = useState(false);
  const [mapData, setMapData] = useState(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [hiddenLanes, setHiddenLanes] = useState(() => new Set());
  const [clientColorLanes, setClientColorLanes] = useState(() => new Set());
  const [verticalName, setVerticalName] = useState('');
  const [verticalOptions, setVerticalOptions] = useState([]);
  const [ignoreLocked, setIgnoreLocked] = useState(false);
  const [sweepDialogOpen, setSweepDialogOpen] = useState(false);
  const [sweepInput, setSweepInput] = useState('0,0.05,0.1,0.2,0.3,0.4,0.5');
  const [sweepLoading, setSweepLoading] = useState(false);
  const [sweepResults, setSweepResults] = useState([]);
  const [conflictSort, setConflictSort] = useState('conflicts'); // conflicts | alpha
  const [conflictTop, setConflictTop] = useState('20'); // 'all' or number as string
  const [conflictSearch, setConflictSearch] = useState('');
  const [reverting, setReverting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [removingClientId, setRemovingClientId] = useState(null);
  const [simulateVerticals, setSimulateVerticals] = useState([]);
  const [simulateClients, setSimulateClients] = useState([]);
  const loadRuns = useCallback(async () => {
    if (!verticalId) {
      setRuns([]);
      return;
    }
    setRunsLoading(true);
    try {
      const res = await fetchLaneRuns({ vertical_id: verticalId });
      setRuns(res.data || []);
    } catch (err) {
      // silent fail
    } finally {
      setRunsLoading(false);
    }
  }, [verticalId]);

  const fetchData = useCallback(async () => {
    if (!verticalId) return;
      setLoading(true);
      setError(null);
      try {
        const res = await fetchLanes({ vertical_id: verticalId });
        const processed =
          (res.data || [])
            .map((lane) => {
              const sortedAssignments = [...(lane.assignments || [])].sort(
                (a, b) => Number(b.spend_weight || 0) - Number(a.spend_weight || 0)
              );
              const lane_spend =
                sortedAssignments.reduce((sum, a) => sum + Number(a.spend_weight || 0), 0) || 0;
              return {
                ...lane,
                assignments: sortedAssignments,
                lane_spend,
                total_zip_count: sortedAssignments.reduce((sum, a) => sum + (a.zip_count || 0), 0) || 0,
                assignment_count: sortedAssignments.length || 0,
                client_count: new Set(sortedAssignments.map((a) => a.client_id)).size,
              };
            })
            .sort((a, b) => {
              if (b.lane_spend !== a.lane_spend) return b.lane_spend - a.lane_spend;
              return (a.name || '').localeCompare(b.name || '');
            });
        setLanes(processed);
      } catch (err) {
        setError(err?.response?.data?.detail || 'Unable to load lanes');
      } finally {
        setLoading(false);
      }
  }, [verticalId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    loadRuns();
  }, [verticalId, rebuilding, loadRuns]);

  const {
    options: clientOptions,
    handleInputChange: handleClientInputChange,
  } = useClientAutocomplete();

  const handleLockToggle = async (assignment) => {
    setLocking(true);
    try {
      await updateLaneAssignment(assignment.id, { locked: !assignment.locked });
      await fetchData();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Unable to update lock state');
    } finally {
      setLocking(false);
    }
  };

  const handleMove = async (assignment, laneId) => {
    if (laneId === 'create-new') {
      return handleCreateAndMove(assignment);
    }
    setMoving(true);
    try {
      await updateLaneAssignment(assignment.id, { lane: laneId });
      await fetchData();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Unable to move assignment');
    } finally {
      setMoving(false);
    }
  };

  const handleCreateAndMove = async (assignment) => {
    if (!verticalId) {
      setError('Pick a vertical first.');
      return;
    }
    setCreatingLane(true);
    setError(null);
    try {
      const laneName = `Manual lane - ${assignment.client_name}`;
      const res = await createLane({
        vertical_id: verticalId,
        name: laneName,
        notes: `Created from UI for ${assignment.client_name}`,
        reserved: true,
      });
      const newLaneId = res.data?.id || res.data?.lane?.id;
      if (newLaneId) {
        await updateLaneAssignment(assignment.id, { lane: newLaneId, locked: true });
        await fetchData();
      } else {
        setError('Lane creation did not return an id.');
      }
    } catch (err) {
      setError(err?.response?.data?.detail || 'Unable to create lane and move assignment');
    } finally {
      setCreatingLane(false);
    }
  };

  const handleDeleteLane = async (laneId) => {
    setDeletingLaneId(laneId);
    setError(null);
    try {
      await deleteLane(laneId);
      await fetchData();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Unable to delete lane');
    } finally {
      setDeletingLaneId(null);
    }
  };

  const handleRemoveClient = async (assignment) => {
    if (!verticalId || !assignment?.client_id) return;
    const clientName = assignment.client_name || `Client ${assignment.client_id}`;
    if (!window.confirm(`Remove ${clientName} from all lanes in this vertical?`)) return;

    setRemovingClientId(assignment.client_id);
    setError(null);
    try {
      await removeClientFromLanes({
        vertical_id: verticalId,
        client_id: assignment.client_id,
      });
      await fetchData();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Unable to remove client from lanes');
    } finally {
      setRemovingClientId(null);
    }
  };

  const handleLaneEditStart = (lane) => {
    setEditingLaneId(lane.id);
    setEditingLaneName(lane.name);
  };

  const handleLaneEditCancel = () => {
    setEditingLaneId(null);
    setEditingLaneName('');
  };

  const handleLaneEditSave = async (laneId) => {
    if (!editingLaneName.trim()) return;
    setSavingLane(true);
    setError(null);
    try {
      await updateLane(laneId, { name: editingLaneName });
      await fetchData();
      handleLaneEditCancel();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Unable to update lane name');
    } finally {
      setSavingLane(false);
    }
  };

  const handleReservedToggle = async (lane, nextValue) => {
    if (!lane?.id) return;
    setReservingLaneId(lane.id);
    setError(null);
    try {
      await updateLane(lane.id, { reserved: nextValue });
      await fetchData();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Unable to update lane setting');
    } finally {
      setReservingLaneId(null);
    }
  };

  const handleRebuild = async () => {
    if (!verticalId) {
      setError('Pick a vertical to build lanes.');
      return;
    }
    setRebuilding(true);
    setError(null);
    try {
      const clientIds = (selectedClients || []).map((opt) => opt.value);

      await rebuildLanes({
        vertical_id: verticalId,
        include_territories: false,
        mode: rebuildMode,
        client_ids: clientIds,
        max_overlap_ratio: overlapTolerance,
        ignore_locked: ignoreLocked,
      });
      await fetchData();
      setRebuildDialogOpen(false);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Unable to rebuild lanes');
    } finally {
      setRebuilding(false);
    }
  };

  const handleSimulate = async () => {
    const { zips, clients, rows } = parseSimulationInput(zipInput);
    const selectedVerticals = simulateVerticals.length
      ? simulateVerticals
      : verticalId
      ? verticalOptions.filter((v) => String(v.id) === String(verticalId))
      : [];
    const verticalIds = selectedVerticals.map((v) => v.id);
    const clientIds = (simulateClients || []).map((opt) => opt.value);

    if (!verticalIds.length) {
      setError('Pick at least one vertical first.');
      return;
    }
    if (!zips.length && !clients.length && !clientIds.length) {
      setError('Add at least one ZIP or client.');
      return;
    }
    setError(null);
    try {
      const res = await simulateLanes({
        vertical_ids: verticalIds,
        zips,
        clients,
        client_ids: clientIds,
        rows,
        max_overlap_ratio: overlapTolerance,
      });
      setSimulation(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Simulation failed');
    }
  };

  const handleConflicts = async (client = null) => {
    if (!verticalId) {
      setError('Pick a vertical first.');
      return;
    }
    setConflictsClient(client);
    setConflictsLoading(true);
    setError(null);
    try {
      const res = await fetchLaneConflicts({ vertical_id: verticalId });
      setConflictsData(res.data);
      setConflictsDialogOpen(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Unable to load conflicts');
    } finally {
      setConflictsLoading(false);
    }
  };

  const handleMap = async () => {
    if (!verticalId) {
      setError('Pick a vertical first.');
      return;
    }
    setMapLoading(true);
    setError(null);
    try {
      const res = await fetchLaneMap({ vertical_id: verticalId });
      setMapData(res.data);
      setMapDialogOpen(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Unable to load map');
    } finally {
      setMapLoading(false);
    }
  };
  const simulationResults = useMemo(() => {
    if (!simulation) return [];
    if (simulation.verticals?.length) return simulation.verticals;
    if (simulation.overlap || simulation.lanes) {
      return [
        {
          vertical_id: verticalId,
          vertical_name: verticalName,
          overlap: simulation.overlap,
          lanes: simulation.lanes,
        },
      ];
    }
    return [];
  }, [simulation, verticalId, verticalName]);
  const laneColorMap = useMemo(() => {
    const map = {};
    (mapData?.lanes || []).forEach((lane) => {
      map[lane.lane_id] = lane.color || '#1d4ed8';
    });
    return map;
  }, [mapData]);

  const laneIds = useMemo(() => (mapData?.lanes || []).map((lane) => lane.lane_id), [mapData]);

  const { clientColorMap, clientNameMap, laneClientsMap } = useMemo(() => {
    const palette = [
      '#0ea5e9',
      '#f97316',
      '#10b981',
      '#6366f1',
      '#ec4899',
      '#14b8a6',
      '#f59e0b',
      '#8b5cf6',
      '#ef4444',
      '#22c55e',
    ];
    const colorMap = {};
    const nameMap = {};
    const laneMap = {};
    (mapData?.lanes || []).forEach((lane) => {
      (lane.features || []).forEach((feature) => {
        const cid = feature?.properties?.client_id;
        const cname = feature?.properties?.client_name || `Client ${cid}`;
        if (!cid) return;
        if (!colorMap[cid]) {
          colorMap[cid] = palette[Math.abs(cid) % palette.length];
        }
        nameMap[cid] = cname;
        laneMap[lane.lane_id] = laneMap[lane.lane_id] || new Set();
        laneMap[lane.lane_id].add(cid);
      });
    });
    return { clientColorMap: colorMap, clientNameMap: nameMap, laneClientsMap: laneMap };
  }, [mapData]);

  const makeOnEachFeature = useCallback(
    (lane) =>
      (feature, layer) => {
        const props = feature?.properties || {};
        const client = props.client_name || 'Client';
        const zip = props.zip || '';
        const laneName = props.lane_name || lane?.lane_name || '';
        const html = `<div><strong>${client}</strong><div>${laneName}</div><div>ZIP: ${zip}</div></div>`;
        layer.bindTooltip(html, { sticky: true, direction: 'top', opacity: 0.9 });
      },
    []
  );

  useEffect(() => {
    const loadVerticals = async () => {
      try {
        const res = await fetchVerticals();
        const opts = (res.data || [])
          .slice()
          .sort((a, b) =>
            (a.vertical || a.name || '').localeCompare(b.vertical || b.name || '', undefined, { sensitivity: 'base' })
          );
        setVerticalOptions(opts);
      } catch (_e) {
        // swallow
      }
    };
    loadVerticals();
  }, []);

  useEffect(() => {
    if (!verticalId || !verticalOptions.length) {
      setVerticalName('');
      return;
    }
    const match = verticalOptions.find((v) => String(v.id) === String(verticalId));
    setVerticalName(match?.vertical || '');
  }, [verticalId, verticalOptions]);

  useEffect(() => {
    if (!simulateDialogOpen) return;
    if (simulateVerticals.length || !verticalId || !verticalOptions.length) return;
    const match = verticalOptions.find((v) => String(v.id) === String(verticalId));
    if (match) {
      setSimulateVerticals([match]);
    }
  }, [simulateDialogOpen, simulateVerticals.length, verticalId, verticalOptions]);

  const conflictNameStats = useMemo(() => {
    const stats = {};
    if (!conflictsData?.pairs?.length) return stats;
    conflictsData.pairs.forEach((p) => {
      stats[p.a_name] = (stats[p.a_name] || 0) + (p.shared_count || 0);
      stats[p.b_name] = (stats[p.b_name] || 0) + (p.shared_count || 0);
    });
    return stats;
  }, [conflictsData]);

  const conflictNames = useMemo(() => {
    if (!conflictsData?.pairs?.length) return [];
    let names = Array.from(new Set(conflictsData.pairs.flatMap((p) => [p.a_name, p.b_name])));
    const term = conflictSearch.trim().toLowerCase();
    if (term) {
      names = names.filter((n) => n.toLowerCase().includes(term));
    }
    names.sort((a, b) => {
      if (conflictSort === 'conflicts') {
        const da = conflictNameStats[a] || 0;
        const db = conflictNameStats[b] || 0;
        if (db !== da) return db - da;
      }
      return (a || '').localeCompare(b || '');
    });
    const topVal = conflictTop === 'all' ? null : parseInt(conflictTop, 10);
    if (topVal && names.length > topVal) {
      names = names.slice(0, topVal);
    }
    return names;
  }, [conflictsData, conflictSort, conflictTop, conflictSearch, conflictNameStats]);

  const maxSharedCount = useMemo(() => {
    if (!conflictsData?.pairs?.length) return 1;
    return Math.max(...conflictsData.pairs.map((p) => p.shared_count || 0), 1);
  }, [conflictsData]);

  const handleSweep = async () => {
    if (!verticalId) {
      setError('Pick a vertical first.');
      return;
    }
    setSweepLoading(true);
    setError(null);
    try {
      const res = await fetchLaneSweep({
        vertical_id: verticalId,
        tolerances: sweepInput || undefined,
        ignore_locked: ignoreLocked,
      });
      setSweepResults(res.data?.results || []);
      setSweepDialogOpen(true);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Unable to run sweep');
    } finally {
      setSweepLoading(false);
    }
  };

  const handleExport = async () => {
    if (!verticalId) {
      setError('Pick a vertical first.');
      return;
    }
    setExporting(true);
    setError(null);
    try {
      const res = await fetchLanesExport({ vertical_id: verticalId });
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const filename = verticalName ? `lanes_${verticalName}.csv` : `lanes_vertical_${verticalId}.csv`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Unable to export lanes');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box sx={{ px: 2, py: 2 }}>
      <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" mb={2}>
        <Box sx={{ minWidth: 220, maxWidth: 260 }}>
          <VerticalDropdown
            value={verticalId}
            onChange={(value) => {
              setVerticalId(value);
              const match = verticalOptions.find((v) => String(v.id) === String(value));
              setVerticalName(match?.vertical || '');
            }}
          />
        </Box>
        <Button variant="contained" startIcon={<RefreshIcon />} disabled={!verticalId || loading} onClick={() => setRebuildDialogOpen(true)}>
          Rebuild lanes
        </Button>
        <Button
          variant="outlined"
          startIcon={<PlaylistAddCheckIcon />}
          disabled={!verticalId || loading}
          onClick={fetchData}
        >
          Refresh
        </Button>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          disabled={!verticalId || exporting}
          onClick={handleExport}
        >
          {exporting ? 'Exporting…' : 'Export CSV'}
        </Button>
        <Button variant="outlined" onClick={() => setSimulateDialogOpen(true)}>
          Test Zip Codes
        </Button>
        <Button variant="outlined" onClick={handleConflicts} disabled={!verticalId || conflictsLoading}>
          Conflict heatmap
        </Button>
        <Button
          variant="outlined"
          onClick={() => handleMap()}
          disabled={!verticalId || mapLoading}
          startIcon={mapLoading ? <CircularProgress size={16} /> : null}
        >
          {mapLoading ? 'Loading map…' : 'Geo map'}
        </Button>
        <Button variant="outlined" onClick={handleSweep} disabled={!verticalId || sweepLoading}>
          {sweepLoading ? 'Running…' : 'Tolerance sweep'}
        </Button>
        <Button
          variant="outlined"
          color="error"
          disabled={!verticalId || reverting}
          onClick={async () => {
            if (!window.confirm('Revert to the last saved lanes snapshot?')) return;
            setReverting(true);
            setError(null);
            try {
              await revertLanes({ vertical_id: verticalId });
              await fetchData();
              await loadRuns();
            } catch (err) {
              setError(err?.response?.data?.detail || 'Unable to revert lanes');
            } finally {
              setReverting(false);
            }
          }}
        >
          {reverting ? 'Reverting…' : 'Revert lanes'}
        </Button>
      </Stack>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Section
        title={verticalName ? `Lanes • ${verticalName}` : 'Lanes'}
        action={
          <Typography variant="body2" color="text.secondary">
            {rebuilding ? 'Rebuilding…' : loading ? 'Loading…' : ''}
          </Typography>
        }
      >
        {lanes.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {verticalId ? 'No lanes yet. Rebuild to create them.' : 'Pick a vertical to view lanes.'}
          </Typography>
        ) : (
          <Grid container spacing={2}>
            {lanes.map((lane) => (
              <Grid item xs={12} md={6} lg={4} key={lane.id || lane.name}>
                <LaneCard
                  lane={lane}
                  lanes={lanes}
                  verticalName={verticalName}
                  onLockToggle={handleLockToggle}
                  onMove={handleMove}
                  onConflictsForClient={(client) => handleConflicts(client)}
                  onEditStart={handleLaneEditStart}
                  onEditCancel={handleLaneEditCancel}
                  onEditSave={handleLaneEditSave}
                  editingLaneId={editingLaneId}
                  editingLaneName={editingLaneName}
                  setEditingLaneName={setEditingLaneName}
                  savingLane={savingLane}
                  onDeleteLane={handleDeleteLane}
                  deletingLaneId={deletingLaneId}
                  reservingLaneId={reservingLaneId}
                  onReservedToggle={handleReservedToggle}
                  onRemoveClient={handleRemoveClient}
                  removingClientId={removingClientId}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Section>

      <Box sx={{ mt: 3 }}>
        <Section title="Recent lane runs" action={runsLoading ? <Typography variant="body2">Loading…</Typography> : null}>
        {runs.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No recent runs.
          </Typography>
        ) : (
          <Stack spacing={1}>
            {runs.slice(0, 10).map((run) => (
              <Stack key={run.id} direction="row" spacing={2} alignItems="center">
                <Chip
                  size="small"
                  label={run.mode}
                  sx={{
                    backgroundColor:
                      run.mode === 'full'
                        ? 'rgba(34,197,94,0.15)'
                        : run.mode === 'incremental'
                        ? 'rgba(59,130,246,0.15)'
                        : run.mode === 'revert'
                        ? 'rgba(239,68,68,0.15)'
                        : 'rgba(148,163,184,0.2)',
                    color:
                      run.mode === 'full'
                        ? '#166534'
                        : run.mode === 'incremental'
                        ? '#1d4ed8'
                        : run.mode === 'revert'
                        ? '#b91c1c'
                        : '#334155',
                    border: '1px solid rgba(0,0,0,0.05)',
                  }}
                />
                <Typography variant="body2" color="text.secondary">
                  {new Date(run.created_at).toLocaleString()} • overlap tol {run.max_overlap_ratio}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}
        </Section>
      </Box>

      <Dialog open={rebuildDialogOpen} onClose={() => setRebuildDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Rebuild lanes</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <Typography variant="body2" color="text.secondary">
              Choose rebuild mode and options.
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                variant={rebuildMode === 'full' ? 'contained' : 'outlined'}
                onClick={() => setRebuildMode('full')}
              >
                Full rebuild
              </Button>
              <Button
                variant={rebuildMode === 'incremental' ? 'contained' : 'outlined'}
                onClick={() => setRebuildMode('incremental')}
              >
                Incremental
              </Button>
            </Stack>
            <Button
              size="small"
              variant="text"
              onClick={() => setShowAdvanced((prev) => !prev)}
              sx={{ alignSelf: 'flex-start' }}
            >
              {showAdvanced ? 'Hide advanced' : 'Show advanced'}
            </Button>
            {showAdvanced ? (
              <TextField
                label="Overlap tolerance (0 - 0.5)"
                type="number"
                size="small"
                inputProps={{ step: 0.01, min: 0, max: 0.5 }}
                value={overlapTolerance}
                onChange={(e) => setOverlapTolerance(Number(e.target.value || 0))}
              />
            ) : null}
            {rebuildMode === 'full' && showAdvanced ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <input
                  type="checkbox"
                  id="ignore-locked"
                  checked={ignoreLocked}
                  onChange={(e) => setIgnoreLocked(e.target.checked)}
                />
                <Typography variant="body2">Ignore locks and move all clients</Typography>
              </Stack>
            ) : null}
            {rebuildMode === 'incremental' ? (
              <Autocomplete
                multiple
                options={clientOptions}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                value={selectedClients}
                onChange={(e, val) => setSelectedClients(val || [])}
                onInputChange={handleClientInputChange}
                renderInput={(params) => <TextField {...params} label="Clients to insert" size="small" />}
              />
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRebuildDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleRebuild}
            disabled={rebuilding || !verticalId}
            startIcon={rebuilding ? <RefreshIcon fontSize="small" /> : null}
          >
            Rebuild
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={simulateDialogOpen} onClose={() => setSimulateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Simulate a new ad</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <Autocomplete
              multiple
              options={verticalOptions}
              getOptionLabel={(option) => option.vertical || option.name || `Vertical ${option.id}`}
              isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
              value={simulateVerticals}
              onChange={(e, val) => setSimulateVerticals(val || [])}
              renderInput={(params) => (
                <TextField {...params} label="Verticals to test" size="small" />
              )}
            />
            <Autocomplete
              multiple
              options={clientOptions}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              value={simulateClients}
              onChange={(e, val) => setSimulateClients(val || [])}
              onInputChange={handleClientInputChange}
              renderInput={(params) => (
                <TextField {...params} label="Clients to include (optional)" size="small" />
              )}
            />
            <TextField
              label="ZIPs or client names/IDs (comma or newline separated)"
              multiline
              minRows={4}
              value={zipInput}
              onChange={(e) => setZipInput(e.target.value)}
              placeholder={"20860  Alco\n20861  Alco\n85001, 85002, 85003\nAcme Roofing"}
              helperText="Tip: paste two columns as ZIP <space> Client Name."
              InputProps={{
                sx: {
                  fontFamily:
                    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                },
              }}
            />
            <Button
              variant="contained"
              onClick={handleSimulate}
              disabled={!simulateVerticals.length && !verticalId}
            >
              Check overlap
            </Button>
            {simulation ? (
              <Stack spacing={1}>
                {simulation.input_clients?.length ? (
                  <Typography variant="body2" color="text.secondary">
                    Included clients: {simulation.input_clients.map((client) => client.name).join(', ')}
                  </Typography>
                ) : null}
                {simulation.unknown_clients?.length ? (
                  <Alert severity="info">
                    New client names included: {simulation.unknown_clients.join(', ')}
                  </Alert>
                ) : null}
                {simulationResults.map((result, index) => (
                  <Box key={`sim-${result.vertical_id || result.vertical_name || index}`}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                      {result.vertical_name || `Vertical ${result.vertical_id || ''}`}
                    </Typography>
                    {(result.input_clients || []).length ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Input clients: {result.input_clients.map((client) => `${client.client_name} (${client.zip_count})`).join(', ')}
                      </Typography>
                    ) : null}
                    {result.lane_estimate ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Lane estimate: +{result.lane_estimate.new_lane_count} new lane(s) needed • {result.lane_estimate.assigned_to_existing} input client(s) fit existing lanes • Total lanes after: {result.lane_estimate.total_lanes_after}
                      </Typography>
                    ) : null}
                    <Typography variant="subtitle2" fontWeight={700}>
                      Lane options
                    </Typography>
                    {(result.lanes || []).length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No lanes are defined for this vertical.
                      </Typography>
                    ) : (
                      (result.lanes || []).map((lane) => (
                        <Box
                          key={`${result.vertical_id || 'v'}-${lane.id || lane.name}`}
                          sx={{
                            border: '1px solid',
                            borderColor: lane.blocked ? 'error.light' : 'divider',
                            backgroundColor: lane.blocked ? 'rgba(244, 67, 54, 0.06)' : 'background.paper',
                            borderRadius: 1.5,
                            px: 1.25,
                            py: 1,
                          }}
                        >
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography fontWeight={700}>{lane.name}</Typography>
                            <Chip
                              size="small"
                              color={lane.blocked ? 'error' : 'success'}
                              label={lane.blocked ? 'Conflict' : 'Safe'}
                            />
                          </Stack>
                          {lane.blocking_clients?.length ? (
                            <Typography variant="body2" color="text.secondary">
                              Conflicts with: {lane.blocking_clients.map((c) => c.client_name).join(', ')}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              No conflicts detected.
                            </Typography>
                          )}
                        </Box>
                      ))
                    )}
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" fontWeight={700}>
                      Overlap details
                    </Typography>
                    {(result.overlap?.clients || []).length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No existing clients in these ZIPs.
                      </Typography>
                    ) : (
                      result.overlap.clients.slice(0, 5).map((client) => (
                        <Box key={client.client_id} sx={{ borderBottom: '1px solid', borderColor: 'divider', py: 1 }}>
                          <Typography fontWeight={700}>{client.client_name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Overlap ZIP/verticals: {client.overlap_zip_verticals} • Overlap % of input: {client.overlap_percentage_of_input}%
                          </Typography>
                        </Box>
                      ))
                    )}
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" fontWeight={700}>
                      Input client overlap
                    </Typography>
                    {(result.input_client_pairs || []).length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No overlap among input clients.
                      </Typography>
                    ) : (
                      result.input_client_pairs.slice(0, 5).map((pair) => (
                        <Box key={`${pair.a_name}-${pair.b_name}`} sx={{ borderBottom: '1px solid', borderColor: 'divider', py: 1 }}>
                          <Typography fontWeight={700}>
                            {pair.a_name} ↔ {pair.b_name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Shared ZIPs: {pair.shared_count} ({pair.shared_sample?.join(', ') || 'n/a'}) · {pair.a_overlap_pct ?? 0}% of {pair.a_name} / {pair.b_overlap_pct ?? 0}% of {pair.b_name}
                          </Typography>
                        </Box>
                      ))
                    )}
                    {index < simulationResults.length - 1 ? <Divider sx={{ my: 2 }} /> : null}
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Paste ZIPs to see which lanes are safe and who would conflict.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSimulateDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={sweepDialogOpen} onClose={() => setSweepDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontSize: 20, fontWeight: 700 }}>Overlap tolerance sweep</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Runs the planner for multiple tolerances without saving changes. Enter comma-separated values or leave
              blank for defaults (0 to 0.5 in 0.05 steps).
            </Typography>
            <TextField
              label="Tolerances"
              value={sweepInput}
              onChange={(e) => setSweepInput(e.target.value)}
              placeholder="0,0.05,0.1,0.2,0.3,0.4,0.5"
            />
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2">Ignore locks?</Typography>
              <Select
                size="small"
                value={ignoreLocked ? 'yes' : 'no'}
                onChange={(e) => setIgnoreLocked(e.target.value === 'yes')}
              >
                <MenuItem value="no">No</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
              </Select>
            </Stack>
            {sweepLoading ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={18} />
                <Typography variant="body2" color="text.secondary">
                  Running…
                </Typography>
              </Stack>
            ) : null}
            {sweepResults.length ? (
              <Box sx={{ overflowX: 'auto' }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(6, 1fr)',
                    gap: 1,
                    minWidth: 600,
                  }}
                >
                  <Typography variant="caption" fontWeight={700}>
                    Tolerance
                  </Typography>
                  <Typography variant="caption" fontWeight={700}>
                    Lanes
                  </Typography>
                  <Typography variant="caption" fontWeight={700}>
                    Clients
                  </Typography>
                  <Typography variant="caption" fontWeight={700}>
                    ZIPs
                  </Typography>
                  <Typography variant="caption" fontWeight={700}>
                    Total spend
                  </Typography>
                  <Typography variant="caption" fontWeight={700}>
                    Top lane spend
                  </Typography>
                  {sweepResults.map((row) => (
                    <React.Fragment key={`row-${row.tolerance}`}>
                      <Typography variant="body2">{row.tolerance}</Typography>
                      <Typography variant="body2">{row.lane_count}</Typography>
                      <Typography variant="body2">{row.client_count}</Typography>
                      <Typography variant="body2">{row.zip_count}</Typography>
                      <Typography variant="body2">
                        ${Number(row.total_spend || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </Typography>
                      <Typography variant="body2">
                        ${Number(row.top_lane_spend || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                      </Typography>
                    </React.Fragment>
                  ))}
                </Box>
              </Box>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSweepDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={conflictsDialogOpen}
        onClose={() => setConflictsDialogOpen(false)}
        maxWidth={false}
        fullWidth
        PaperProps={{ sx: { maxWidth: '96vw' } }}
      >
        <DialogTitle sx={{ fontSize: 20, fontWeight: 700 }}>Lane conflicts</DialogTitle>
        <DialogContent dividers>
          {conflictsLoading ? (
            <Typography variant="body2" color="text.secondary">
              Loading...
            </Typography>
          ) : conflictsData ? (
            <Stack spacing={2}>
              <Typography variant="subtitle2" fontWeight={700}>
                Conflict heatmap
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Cells show shared ZIP count. Hover to see overlap percentages (row % / column %).
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <TextField
                  label="Filter clients"
                  size="small"
                  value={conflictSearch}
                  onChange={(e) => setConflictSearch(e.target.value)}
                  sx={{ minWidth: 220 }}
                />
                <Stack direction="row" spacing={1} alignItems="center">
                  <InputLabel shrink>Sort</InputLabel>
                  <Select
                    size="small"
                    value={conflictSort}
                    onChange={(e) => setConflictSort(e.target.value)}
                    sx={{ minWidth: 160 }}
                  >
                    <MenuItem value="conflicts">Most conflicts</MenuItem>
                    <MenuItem value="alpha">Alphabetical</MenuItem>
                  </Select>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <InputLabel shrink>Show</InputLabel>
                  <Select
                    size="small"
                    value={conflictTop}
                    onChange={(e) => setConflictTop(e.target.value)}
                    sx={{ minWidth: 140 }}
                  >
                    <MenuItem value="10">Top 10</MenuItem>
                    <MenuItem value="20">Top 20</MenuItem>
                    <MenuItem value="30">Top 30</MenuItem>
                    <MenuItem value="50">Top 50</MenuItem>
                    <MenuItem value="all">All</MenuItem>
                  </Select>
                </Stack>
              </Stack>
              {conflictsData.pairs?.length ? (
                <Box
                  sx={{
                    overflowX: 'auto',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1.5,
                    p: 2,
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  {(() => {
                    const colWidth = 140;
                    const nameWidth = 170;
                    return (
                      <Box
                        sx={{
                          minWidth: nameWidth + conflictNames.length * colWidth,
                          display: 'grid',
                          gridTemplateColumns: `${nameWidth}px repeat(${conflictNames.length}, ${colWidth}px)`,
                          rowGap: 0.5,
                          columnGap: 0.5,
                        }}
                      >
                        <Box />
                        {conflictNames.map((name) => (
                          <Box
                            key={`col-${name}`}
                            sx={{
                              px: 1,
                              py: 0.5,
                              position: 'sticky',
                              top: 0,
                              zIndex: 3,
                              backgroundColor: 'background.paper',
                              height: 70,
                              display: 'flex',
                              alignItems: 'flex-end',
                              justifyContent: 'center',
                            }}
                          >
                            <Typography
                              variant="body2"
                              fontWeight={600}
                              sx={{
                                transform: 'rotate(-45deg)',
                                transformOrigin: 'left bottom',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {name}
                            </Typography>
                          </Box>
                        ))}
                        {conflictNames.map((rowName) => (
                          <React.Fragment key={`row-${rowName}`}>
                            <Box
                              sx={{
                                px: 1,
                                py: 0.5,
                                display: 'flex',
                                alignItems: 'center',
                                position: 'sticky',
                                left: 0,
                                zIndex: 2,
                                backgroundColor: 'background.paper',
                              }}
                            >
                              <Typography variant="body2" fontWeight={600}>
                                {rowName}
                              </Typography>
                            </Box>
                            {conflictNames.map((colName) => {
                              if (rowName === colName) {
                                return <Box key={`${rowName}-${colName}`} />;
                              }
                              const match = conflictsData.pairs.find(
                                (p) =>
                                  (p.a_name === rowName && p.b_name === colName) ||
                                  (p.a_name === colName && p.b_name === rowName)
                              );
                              const intensity = match ? Math.min(1, 0.15 + 0.85 * (match.shared_count / maxSharedCount)) : 0;
                              return (
                                <Tooltip
                                  key={`${rowName}-${colName}`}
                                  title={
                                    match
                                      ? `${match.shared_count} shared ZIPs • ${match.a_overlap_pct ?? 0}% of ${rowName} / ${match.b_overlap_pct ?? 0}% of ${colName}`
                                      : ''
                                  }
                                  placement="top"
                                  arrow
                                  disableInteractive={!match}
                                >
                                  <Box
                                    sx={{
                                      bgcolor: match ? `rgba(76, 175, 80, ${intensity})` : 'transparent',
                                      border: match ? '1px solid #4caf50' : '1px dashed rgba(0,0,0,0.1)',
                                      borderRadius: 0.75,
                                      minHeight: 34,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      px: 1,
                                      textAlign: 'center',
                                      fontSize: '0.85rem',
                                      lineHeight: 1.2,
                                    }}
                                  >
                                    {match ? match.shared_count : ''}
                                  </Box>
                                </Tooltip>
                              );
                            })}
                          </React.Fragment>
                        ))}
                      </Box>
                    );
                  })()}
                </Box>
              ) : null}
              {conflictsData.pairs?.length ? (
                (conflictsClient
                  ? conflictsData.pairs.filter(
                      (pair) =>
                        pair.a_id === conflictsClient.client_id ||
                        pair.b_id === conflictsClient.client_id
                    )
                  : conflictsData.pairs
                )
                  .slice(0, 50)
                  .map((pair, idx) => (
                  <Box key={`${pair.a_id}-${pair.b_id}-${idx}`} sx={{ borderBottom: '1px solid', borderColor: 'divider', pb: 1, mb: 1 }}>
                    <Typography fontWeight={700}>
                      {pair.a_name} ↔ {pair.b_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Shared ZIPs: {pair.shared_count} ({pair.shared_sample?.join(', ') || 'n/a'}) · {pair.a_overlap_pct ?? 0}% of {pair.a_name} / {pair.b_overlap_pct ?? 0}% of {pair.b_name}
                    </Typography>
                    <Chip size="small" label={pair.reason} sx={{ mt: 0.5 }} />
                  </Box>
                  ))
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No conflict pairs found.
                </Typography>
              )}
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Select a vertical and click “View conflicts.”
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConflictsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={mapDialogOpen}
        onClose={() => setMapDialogOpen(false)}
        maxWidth="xl"
        fullWidth
        PaperProps={{ sx: { maxWidth: '96vw' } }}
      >
        <DialogTitle sx={{ fontSize: 20, fontWeight: 700 }}>
          {verticalName ? `Lane map • ${verticalName}` : 'Lane map'}
        </DialogTitle>
        <DialogContent dividers>
        {mapLoading ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={18} />
            <Typography variant="body2" color="text.secondary">
              Loading map...
            </Typography>
          </Stack>
        ) : mapData ? (
          <Box sx={{ height: '72vh', borderRadius: 2, overflow: 'hidden', position: 'relative' }}>
            <Box
              sx={{
                position: 'absolute',
                top: 12,
                right: 12,
                zIndex: 1000,
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                p: 1.25,
                borderRadius: 2,
                bgcolor: 'rgba(255,255,255,0.94)',
                boxShadow: '0 10px 25px rgba(15,23,42,0.16)',
                maxWidth: 440,
              }}
            >
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                Lanes
              </Typography>
              <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', mb: 0.5 }}>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setHiddenLanes(() => new Set(laneIds))}
                  disabled={!laneIds.length}
                >
                  Hide all
                </Button>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setHiddenLanes(new Set())}
                  disabled={!laneIds.length}
                >
                  Show all
                </Button>
              </Stack>
              <Stack spacing={0.75}>
                {(mapData.lanes || []).map((lane) => {
                  const hidden = hiddenLanes.has(lane.lane_id);
                  const clientNames = Array.from(laneClientsMap[lane.lane_id] || [])
                    .map((cid) => clientNameMap[cid] || `Client ${cid}`)
                    .join(', ');
                  return (
                    <Stack
                      key={`legend-${lane.lane_id}`}
                      direction="row"
                      spacing={1}
                      alignItems="flex-start"
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        setHiddenLanes((prev) => {
                          const next = new Set(prev);
                          if (next.has(lane.lane_id)) {
                            next.delete(lane.lane_id);
                          } else {
                            next.add(lane.lane_id);
                          }
                          return next;
                        });
                      }}
                    >
                      <Box
                        sx={{
                          width: 16,
                          height: 16,
                          borderRadius: 0.5,
                          backgroundColor: laneColorMap[lane.lane_id] || '#1d4ed8',
                          border: '1px solid rgba(0,0,0,0.12)',
                          opacity: hidden ? 0.3 : 1,
                          mt: 0.4,
                        }}
                      />
                      <Stack spacing={0.2} sx={{ minWidth: 0, flex: 1, opacity: hidden ? 0.5 : 1 }}>
                        <Typography variant="body2">{lane.lane_name}</Typography>
                        {clientNames ? (
                          <Typography variant="caption" color="text.secondary">
                            {clientNames}
                          </Typography>
                        ) : null}
                      </Stack>
                      <Button
                        size="small"
                        variant="text"
                        sx={{ whiteSpace: 'nowrap', alignSelf: 'flex-start' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setHiddenLanes((prev) => {
                            const next = new Set(prev);
                            next.delete(lane.lane_id);
                            return next;
                          });
                          setClientColorLanes((prev) => {
                            const next = new Set(prev);
                            if (next.has(lane.lane_id)) {
                              next.delete(lane.lane_id);
                            } else {
                              next.add(lane.lane_id);
                            }
                            return next;
                          });
                        }}
                      >
                        {clientColorLanes.has(lane.lane_id) ? 'Hide clients' : 'Show clients'}
                      </Button>
                      {clientColorLanes.has(lane.lane_id) ? (
                        <Stack spacing={0.25} sx={{ minWidth: 0, flex: 1 }}>
                          {Array.from(laneClientsMap[lane.lane_id] || []).map((cid) => (
                            <Stack key={`client-chip-${lane.lane_id}-${cid}`} direction="row" spacing={0.75} alignItems="center">
                              <Box
                                sx={{
                                  width: 12,
                                  height: 12,
                                  borderRadius: '50%',
                                  backgroundColor: clientColorMap[cid],
                                  border: '1px solid rgba(0,0,0,0.12)',
                                  mt: 0.25,
                                }}
                              />
                              <Typography variant="caption" color="text.secondary">
                                {clientNameMap[cid] || `Client ${cid}`}
                              </Typography>
                            </Stack>
                          ))}
                        </Stack>
                      ) : null}
                    </Stack>
                  );
                })}
              </Stack>
            </Box>
            <MapContainer
              center={[37.8, -96]}
              zoom={4}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom
            >
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                subdomains={['a', 'b', 'c', 'd']}
                maxZoom={19}
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
              />
              {mapData.lanes
                ?.filter((lane) => !hiddenLanes.has(lane.lane_id))
                .map((lane) => (
                  <GeoJSON
                    key={lane.lane_id}
                    data={{ type: 'FeatureCollection', features: lane.features || [] }}
                    style={(feature) => {
                      const cid = feature?.properties?.client_id;
                      const laneHasClientColors = clientColorLanes.has(lane.lane_id);
                      const color =
                        laneHasClientColors && cid && clientColorMap[cid]
                          ? clientColorMap[cid]
                          : laneColorMap[lane.lane_id] || '#1d4ed8';
                      return {
                        color,
                        weight: 1,
                        fillOpacity: 0.35,
                      };
                    }}
                    onEachFeature={makeOnEachFeature(lane)}
                  />
                ))}
            </MapContainer>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Select a vertical and click “Geo map.”
          </Typography>
        )}
      </DialogContent>
        <DialogActions>
          <Button onClick={() => setMapDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
  </Box>
);
}
export default ClientLanesPage;
